import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useDataRefresh } from '../hooks/useDataRefresh';
import { getRevenue, getDashboardLayout, saveDashboardLayout, resetDashboardLayout } from '../services/api';
import Navbar from '../components/common/Navbar';
import RefreshButton from '../components/common/RefreshButton';
import LoadingSpinner from '../components/common/LoadingSpinner';
import HeroStats from '../components/dashboard/HeroStats';
import StatsCards from '../components/dashboard/StatsCards';
import TopDishes from '../components/dashboard/TopDishes';
import WaitersList from '../components/dashboard/WaitersList';
import RevenueChart from '../components/dashboard/RevenueChart';
import DailyTable from '../components/dashboard/DailyTable';
import PayrollWidget from '../components/dashboard/PayrollWidget';
import AdvisorWidget from '../components/dashboard/AdvisorWidget';
import OnboardingBanner from '../components/dashboard/OnboardingBanner';
import AIForecast from '../components/dashboard/AIForecast';
import RevenueTrendChart from '../components/dashboard/RevenueTrendChart';
import MonthGoalWidget from '../components/dashboard/MonthGoalWidget';
import TodayShiftsWidget from '../components/dashboard/TodayShiftsWidget';
import PeriodReportModal from '../components/dashboard/PeriodReportModal';
import DashboardGrid from '../components/dashboard/DashboardGrid';
import {
  defaultLayout, mergeLayout, isDefaultLayout, showItem,
} from '../components/dashboard/dashboardLayout';
import './DashboardPage.css';

/* Иконки штрихом вместо эмодзи — см. Navbar.jsx, тот же паттерн */
const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const CalendarIcon = () => (
  <Icon><path d="M2 12C2 8.22876 2 6.34315 3.17157 5.17157C4.34315 4 6.22876 4 10 4H14C17.7712 4 19.6569 4 20.8284 5.17157C22 6.34315 22 8.22876 22 12V14C22 17.7712 22 19.6569 20.8284 20.8284C19.6569 22 17.7712 22 14 22H10C6.22876 22 4.34315 22 3.17157 20.8284C2 19.6569 2 17.7712 2 14V12Z" /><path d="M7 4V2.5" /><path d="M17 4V2.5" /><path d="M2.5 9H21.5" /><path strokeLinejoin="round" d="M17 13H17.0001" /><path strokeLinejoin="round" d="M12 13H12.0001" /><path strokeLinejoin="round" d="M7 13H7.0001" /><path strokeLinejoin="round" d="M17 17.0234H17.0001" /><path strokeLinejoin="round" d="M12 17.0234H12.0001" /><path strokeLinejoin="round" d="M7 17.0234H7.0001" /></Icon>
);

const HourglassIcon = () => (
  <Icon><path d="M12 12L9.0423 14.9289C6.11981 17.823 4.65857 19.27 5.06765 20.5185C5.10282 20.6258 5.14649 20.7302 5.19825 20.8307C5.80046 22 7.86697 22 12 22C16.133 22 18.1995 22 18.8017 20.8307C18.8535 20.7302 18.8972 20.6258 18.9323 20.5185C19.3414 19.27 17.8802 17.823 14.9577 14.9289L12 12ZM12 12L14.9577 9.07107C17.8802 6.177 19.3414 4.72997 18.9323 3.48149C18.8972 3.37417 18.8535 3.26977 18.8017 3.16926C18.1995 2 16.133 2 12 2C7.86697 2 5.80046 2 5.19825 3.16926C5.14649 3.26977 5.10282 3.37417 5.06765 3.48149C4.65857 4.72997 6.11981 6.177 9.0423 9.07107L12 12Z" /><path d="M10 5.5H14" /><path d="M10 18.5H14" /></Icon>
);

const DangerIcon = () => (
  <Icon><path d="M5.31171 10.7615C8.23007 5.58716 9.68925 3 12 3C14.3107 3 15.7699 5.58716 18.6883 10.7615L19.0519 11.4063C21.4771 15.7061 22.6897 17.856 21.5937 19.428C20.4978 21 17.7864 21 12.3637 21H11.6363C6.21356 21 3.50217 21 2.40626 19.428C1.31034 17.856 2.52291 15.7061 4.94805 11.4063L5.31171 10.7615Z" /><path d="M12 8V13" /><path strokeLinejoin="round" d="M12 16H12.0001" /></Icon>
);

const LayoutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
       strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="8" height="10" rx="2" /><rect x="13" y="3" width="8" height="6" rx="2" />
    <rect x="13" y="11" width="8" height="10" rx="2" /><rect x="3" y="15" width="8" height="6" rx="2" />
  </svg>
);

const ALL = ['third', 'half', 'two_thirds', 'full'];
const WIDE = ['half', 'two_thirds', 'full'];

// Виджеты дашборда в стандартном порядке. Стандартные размеры повторяют
// прежнюю вёрстку, так что у того, кто ничего не настраивал, всё как было.
// roles — кому виджет вообще показывается (без поля — всем с доступом к дашборду).
const WIDGETS = [
  { id: 'hero', title: 'Главные показатели', defaultSize: 'full', sizes: WIDE },
  { id: 'trend', title: 'Динамика выручки', defaultSize: 'full', sizes: WIDE },
  { id: 'goal', title: 'Цель месяца', defaultSize: 'full', sizes: ALL },
  { id: 'shifts', title: 'Сегодня на смене', defaultSize: 'full', sizes: ALL },
  { id: 'forecast', title: 'AI-прогноз', defaultSize: 'full', sizes: WIDE },
  { id: 'advisor', title: 'Советник', defaultSize: 'full', sizes: WIDE, roles: ['admin', 'manager'] },
  { id: 'payroll', title: 'ФОТ', defaultSize: 'full', sizes: WIDE },
  { id: 'stats', title: 'Показатели дня', defaultSize: 'full', sizes: ['half', 'full'] },
  { id: 'top_dishes', title: 'Топ блюд', defaultSize: 'half', sizes: ALL },
  { id: 'waiters', title: 'Официанты', defaultSize: 'half', sizes: ALL },
  { id: 'revenue_chart', title: 'График выручки по дням', defaultSize: 'full', sizes: WIDE },
  { id: 'daily_table', title: 'Таблица по дням', defaultSize: 'full', sizes: WIDE },
];

// Последняя раскладка лежит и в браузере — чтобы дашборд сразу открывался в
// своём виде, а не перестраивался, когда ответит сервер. Источник правды — сервер.
const cacheKey = (user) => `dashboard-layout:${user?.id ?? user?.username ?? 'anon'}`;
const readCache = (user) => {
  try {
    const raw = localStorage.getItem(cacheKey(user));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const writeCache = (user, layout) => {
  try {
    if (layout) localStorage.setItem(cacheKey(user), JSON.stringify(layout));
    else localStorage.removeItem(cacheKey(user));
  } catch { /* приватный режим */ }
};

const DashboardPage = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { data, loading, error, lastUpdated, refresh } = useDataRefresh(getRevenue);
  // data.data === null означает, что iiko не отдал выручку. Раньше в этом
  // случае дашборд рисовал нули, неотличимые от настоящей нулевой выручки.
  // Само наличие ошибки условием не делаем: useDataRefresh держит последний
  // удачный ответ, и при разовом сбое опроса лучше показать его, чем заглушку.
  const noSourceData = !loading && !data?.data;
  const sourceLoading = error?.response?.data?.status === 'loading';
  const [reportOpen, setReportOpen] = useState(false);

  const widgets = useMemo(() => WIDGETS.filter(w => !w.roles || w.roles.includes(user?.role)), [user?.role]);
  const [saved, setSaved] = useState(() => readCache(user));
  const [draft, setDraft] = useState(null);      // не null — идёт настройка
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const layout = useMemo(() => mergeLayout(draft ?? saved, widgets), [draft, saved, widgets]);
  const editing = draft !== null;

  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    setSaved(readCache({ id: userId }));
    getDashboardLayout()
      .then(r => { setSaved(r.layout); writeCache({ id: userId }, r.layout); })
      .catch(() => { /* остаётся раскладка из браузера или стандартная */ });
  }, [userId]);

  const startEditing = () => { setSaveError(''); setDraft(mergeLayout(saved, widgets)); };
  const cancelEditing = () => { setDraft(null); setSaveError(''); };
  const onLayoutChange = useCallback((next) => setDraft(next), []);

  const saveLayout = async () => {
    setSaving(true);
    setSaveError('');
    try {
      // Стандартную раскладку не храним: тогда новые виджеты и правки
      // стандартного порядка доезжают до пользователя без его участия.
      const r = isDefaultLayout(draft, widgets)
        ? await resetDashboardLayout()
        : await saveDashboardLayout(draft);
      setSaved(r.layout);
      writeCache(user, r.layout);
      setDraft(null);
    } catch (e) {
      setSaveError(e?.response?.data?.error || 'Не удалось сохранить раскладку — попробуйте ещё раз');
    } finally {
      setSaving(false);
    }
  };

  const hidden = layout.filter(item => item.hidden);
  const titleOf = (id) => widgets.find(w => w.id === id)?.title || id;

  const renderWidget = (id) => {
    const d = data?.data;
    switch (id) {
      case 'hero': return <HeroStats data={d} />;
      case 'trend': return <RevenueTrendChart />;
      case 'goal': return <MonthGoalWidget />;
      case 'shifts': return <TodayShiftsWidget />;
      case 'forecast': return <AIForecast isAdmin={user?.role === 'admin'} />;
      case 'advisor': return <AdvisorWidget />;
      case 'payroll': return <PayrollWidget />;
      case 'stats': return <StatsCards data={d} />;
      case 'top_dishes': return <TopDishes dishes={d?.top_dishes_today || []} />;
      case 'waiters': return <WaitersList waiters={d?.waiters_today || []} />;
      case 'revenue_chart': return <RevenueChart dailyData={d?.daily_data || []} />;
      case 'daily_table': return <DailyTable dailyData={d?.daily_data || []} />;
      default: return null;
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="dashboard-container">
      <Navbar user={user} isAdmin={user?.role === 'admin'} onLogout={logout} />

      <OnboardingBanner user={user} />

      <div className="dashboard-header">
        <div className="date-badge">
          <span className="date-badge-icon"><CalendarIcon /></span>
          <span>{data?.data?.current_date || '—'}</span>
          <span className="update-time">
            Обновлено: {data?.data?.last_updated || lastUpdated?.toLocaleTimeString() || '—'}
          </span>
        </div>
        <div className="dashboard-header-actions">
          <button type="button" className="ui-btn-ghost" onClick={() => setReportOpen(true)}>
            Отчёт за период
          </button>
          {!editing && !noSourceData && (
            <button type="button" className="ui-btn-ghost" onClick={startEditing}>
              <LayoutIcon /> Настроить
            </button>
          )}
          <RefreshButton onRefresh={refresh} loading={loading} />
        </div>
      </div>

      {reportOpen && <PeriodReportModal onClose={() => setReportOpen(false)} />}

      {loading && !data ? (
        <LoadingSpinner />
      ) : noSourceData ? (
        <div className="panel dashboard-offline">
          <div className={`dashboard-offline-icon ${sourceLoading ? '' : 'danger'}`}>
            {sourceLoading ? <HourglassIcon /> : <DangerIcon />}
          </div>
          <div className="dashboard-offline-text">
            <strong>
              {sourceLoading
                ? 'Загружаем данные из iiko'
                : 'Данные из iiko временно недоступны'}
            </strong>
            <p>
              {sourceLoading
                ? 'Первая выгрузка за день занимает до минуты — обновите страницу чуть позже.'
                : `Выручка, заказы и средний чек не загрузились. Это не нулевая выручка —
                   сервер iiko не ответил. Попробуйте обновить через минуту.`}
            </p>
          </div>
          <button type="button" className="btn-primary" onClick={refresh}>Повторить</button>
        </div>
      ) : (
        <>
          {editing && (
            <div className="dg-editbar" role="region" aria-label="Настройка дашборда">
              <div className="dg-editbar-text">
                <strong>Настройка дашборда</strong>
                Перетаскивайте виджеты за заголовок, меняйте ширину и скрывайте лишнее.
                Раскладка ваша — у коллег она не изменится.
              </div>
              <div className="dg-editbar-actions">
                <button type="button" className="ui-btn-ghost" disabled={saving}
                        onClick={() => setDraft(defaultLayout(widgets))}>
                  Как было
                </button>
                <button type="button" className="ui-btn-ghost" disabled={saving} onClick={cancelEditing}>
                  Отмена
                </button>
                <button type="button" className="ui-btn-primary" disabled={saving} onClick={saveLayout}>
                  {saving ? 'Сохраняем…' : 'Сохранить'}
                </button>
              </div>
              {saveError && <p className="ui-hint-error dg-editbar-error">{saveError}</p>}
            </div>
          )}

          <DashboardGrid
            layout={layout}
            widgets={widgets}
            editing={editing}
            onChange={onLayoutChange}
            renderWidget={renderWidget}
          />

          {editing && (
            <div className="dg-tray">
              <h3 className="dg-tray-title">Скрытые виджеты</h3>
              {hidden.length ? (
                <div className="dg-tray-list">
                  {hidden.map(item => (
                    <button key={item.id} type="button" className="dg-tray-item"
                            onClick={() => setDraft(showItem(layout, item.id))}>
                      + {titleOf(item.id)}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="dg-tray-empty">Все виджеты на дашборде. Скрыть лишний — кнопка с перечёркнутым глазом.</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardPage;
